import { useState } from 'react';

import CustomModal from '@/components/CustomModal';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import {
  STATUS_OPTIONS,
  QUARTER_OPTIONS,
  LESSON_STAGE_OPTIONS,
} from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';

interface IFilterModalProps {
  setIsOpen: (open: boolean) => void;
  filters: { status: string; stage: string; quarter: string };
  setFilters: (filters: {
    status: string;
    stage: string;
    quarter: string;
  }) => void;
  onApply: (filters: {
    status: string;
    stage: string;
    quarter: string;
  }) => void;
}

const FilterModal = ({
  setIsOpen,
  filters,
  setFilters,
  onApply,
}: IFilterModalProps) => {
  const { formatText } = useFormatText();

  const [draft, setDraft] = useState({
    status: filters.status,
    stage: filters.stage,
    quarter: filters.quarter,
  });

  const handleClear = () => {
    const cleared = { status: '', stage: '', quarter: '' };
    setDraft(cleared);
    setFilters(cleared);
    onApply(cleared);
    setIsOpen(false);
  };

  const handleShowResults = () => {
    setFilters(draft);
    onApply(draft);
    setIsOpen(false);
  };

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      heading={formatText('contentManagement.filter-modal.title')}
      isHeader
      isFooter
      position="right"
      cancelButtonProps={{
        label: formatText('contentManagement.filter-modal.clear'),
        variant: 'link',
        onClick: handleClear,
        className: 'text-neutral-secondary hover:text-neutral-primary px-0',
      }}
      confirmButtonProps={{
        label: formatText('contentManagement.filter-modal.show-results'),
        onClick: handleShowResults,
      }}
    >
      <div className="w-full space-y-6">
        <RadioButtonGroup
          name="status"
          label={formatText('contentManagement.filter-modal.status-label')}
          options={STATUS_OPTIONS}
          value={draft.status}
          onChange={(val) =>
            setDraft({ ...draft, status: draft.status === val ? '' : val })
          }
          formatLabel={(label) => formatText(label)}
          gap="gap-6"
        />

        <Select
          name={formatText('contentManagement.filter-modal.stage-label')}
          options={LESSON_STAGE_OPTIONS}
          selected={draft.stage}
          onChange={(val) => setDraft({ ...draft, stage: val as string })}
          placeholder={formatText(
            'contentManagement.edit-modal.stage-placeholder'
          )}
          isClearable
          isTextFormat
        />

        <Select
          name={formatText('contentManagement.filter-modal.quarter-label')}
          options={QUARTER_OPTIONS}
          selected={draft.quarter}
          onChange={(val) => setDraft({ ...draft, quarter: val as string })}
          placeholder={formatText(
            'contentManagement.filter-modal.quarter-placeholder'
          )}
          isClearable
        />
      </div>
    </CustomModal>
  );
};

export default FilterModal;
